<template>
  <div class="questionnaire-view">
    <div class="questionnaire-header">
      <h2>Caso Clínico nº {{ voucherData?.voucherId }}</h2>
      <div class="case-info">
        <p><strong>Especialidade:</strong> {{ voucherData?.specialtyName }}</p>
        <p><strong>Doença:</strong> {{ voucherData?.diseaseName }}</p>
      </div>
    </div>

    <!-- Dados do Paciente -->
    <div class="questionnaire-section">
      <h3>Dados do Paciente</h3>
      <div class="qa-item">
        <p><strong>Idade:</strong> {{ voucherData?.age }} anos</p>
        <p><strong>Gênero:</strong> {{ formatGender(voucherData?.gender) }}</p>
        <p><strong>Cor/Raça:</strong> {{ formatColor(voucherData?.color) }}</p>
      </div>
    </div>

    <!-- Formulário de Asma -->
    <template v-if="isAsmaForm">
      <!-- Diagnóstico -->
      <div class="questionnaire-section">
        <h3>Diagnóstico de Asma Grave</h3>
        <div class="qa-item">
          <p>
            <strong>Diagnóstico de Asma:</strong>
            {{ formatYesNo(voucherData?.asthma) }}
          </p>
          <p>
            <strong>Outras Doenças Pulmonares:</strong>
            {{ formatYesNo(voucherData?.lungDiseases) }}
          </p>
          <p>
            <strong>Adesão ao Tratamento:</strong>
            {{ formatYesNo(voucherData?.asthmaTreatment) }}
          </p>
          <p>
            <strong>Técnica Inalatória Adequada:</strong>
            {{ formatYesNo(voucherData?.inhalation) }}
          </p>
        </div>
      </div>

      <!-- Comorbidades -->
      <div class="questionnaire-section">
        <h3>Comorbidades</h3>
        <div class="qa-item">
          <p>
            <strong>Comorbidades Presentes:</strong>
            {{ formatComorbidities(voucherData?.comorbidities) }}
          </p>
          <p>
            <strong>Outras Comorbidades:</strong>
            {{ voucherData?.otherComorbidities || '-' }}
          </p>
          <p>
            <strong>Informações Adicionais:</strong>
            {{ voucherData?.comorbiditiesInfo || '-' }}
          </p>
        </div>
      </div>

      <!-- Medicações e Tratamentos -->
      <div class="questionnaire-section">
        <h3>Medicações e Tratamentos</h3>
        <div class="qa-item">
          <p>
            <strong>Uso de LABA:</strong> {{ formatYesNo(voucherData?.laba) }}
          </p>
          <p>
            <strong>Medicações para Asma:</strong>
            {{ voucherData?.asthmaMedications || '-' }}
          </p>
          <p>
            <strong>Uso de Imunobiológico:</strong>
            {{ formatYesNo(voucherData?.immunobiological) }}
          </p>
          <p>
            <strong>Descrição do Imunobiológico:</strong>
            {{ voucherData?.immunobiologicalDescription || '-' }}
          </p>
          <p>
            <strong>Uso de Corticoide Oral:</strong>
            {{ formatYesNo(voucherData?.corticosteroid) }}
          </p>
          <p>
            <strong>Frequência de Corticoide Oral:</strong>
            {{ voucherData?.corticosteroidTimes || '-' }}
          </p>
          <p>
            <strong>Medicações em Uso:</strong>
            {{ voucherData?.medications || '-' }}
          </p>
          <p>
            <strong>Descrição das Medicações:</strong>
            {{ voucherData?.medicationsDescription || '-' }}
          </p>
        </div>
      </div>

      <!-- Exames e Avaliações -->
      <div class="questionnaire-section">
        <h3>Exames e Avaliações</h3>
        <div class="qa-item">
          <p>
            <strong>Data da Espirometria:</strong>
            {{ formatDate(voucherData?.espiroDate) }}
          </p>
          <p><strong>CVF Pré:</strong> {{ voucherData?.preCvf || '-' }}</p>
          <p><strong>VEF1 Pré:</strong> {{ voucherData?.preVef || '-' }}</p>
          <p>
            <strong>VEF1/CVF Pré:</strong> {{ voucherData?.preVefCvf || '-' }}
          </p>
          <p>
            <strong>FEF 25-75% Pré:</strong> {{ voucherData?.preFef || '-' }}
          </p>
          <p><strong>CVF Pós:</strong> {{ voucherData?.posCvf || '-' }}</p>
          <p><strong>VEF1 Pós:</strong> {{ voucherData?.posVef || '-' }}</p>
          <p>
            <strong>VEF1/CVF Pós:</strong> {{ voucherData?.posVefCvf || '-' }}
          </p>
          <p>
            <strong>FEF 25-75% Pós:</strong> {{ voucherData?.posFef || '-' }}
          </p>
          <p><strong>Resposta BD:</strong> {{ voucherData?.bd || '-' }}</p>
          <p>
            <strong>Eosinófilos no Sangue:</strong>
            {{ voucherData?.eosinophilsBlood || '-' }}
          </p>
          <p><strong>IgE Total:</strong> {{ voucherData?.igeTotal || '-' }}</p>
          <p><strong>FeNO:</strong> {{ voucherData?.FeNO || '-' }}</p>
        </div>
      </div>

      <!-- Histórico -->
      <div class="questionnaire-section">
        <h3>Histórico</h3>
        <div class="qa-item">
          <p>
            <strong>Hospitalizações:</strong>
            {{ formatYesNo(voucherData?.hospitalization) }}
          </p>
          <p>
            <strong>Número de Hospitalizações:</strong>
            {{ voucherData?.hospitalizationTimes || '-' }}
          </p>
          <p>
            <strong>Internações em UTI:</strong>
            {{ formatYesNo(voucherData?.uti) }}
          </p>
          <p>
            <strong>Número de Internações em UTI:</strong>
            {{ voucherData?.utiTimes || '-' }}
          </p>
          <p>
            <strong>Ventilação Mecânica:</strong>
            {{ formatYesNo(voucherData?.ventilation) }}
          </p>
          <p>
            <strong>Número de Ventilações Mecânicas:</strong>
            {{ voucherData?.ventilationTimes || '-' }}
          </p>
        </div>
      </div>
    </template>

    <!-- Formulário de Crohn -->
    <template v-if="isCrohnForm">
      <!-- Dados Antropométricos -->
      <div class="questionnaire-section">
        <h3>Dados Antropométricos</h3>
        <div class="qa-item">
          <p><strong>Peso:</strong> {{ voucherData?.weight || '-' }} kg</p>
          <p><strong>Altura:</strong> {{ voucherData?.height || '-' }} cm</p>
        </div>
      </div>

      <!-- Informações do Diagnóstico -->
      <div class="questionnaire-section">
        <h3>Informações do Diagnóstico</h3>
        <div class="qa-item">
          <p>
            <strong>Diagnóstico:</strong>
            {{ formatDiagnostico(voucherData?.diag) }}
          </p>
          <p>
            <strong>Idade no Início dos Sintomas:</strong>
            {{ voucherData?.yearStartSint || '-' }} anos
          </p>
          <p>
            <strong>Idade no Diagnóstico:</strong>
            {{ voucherData?.yearDiag || '-' }} anos
          </p>
          <p>
            <strong>Localização Montreal:</strong>
            {{ voucherData?.locationMontreal || '-' }}
          </p>
          <p>
            <strong>Comportamento Montreal:</strong>
            {{ voucherData?.behaviorMontreal || '-' }}
          </p>
          <p><strong>Extensão:</strong> {{ voucherData?.extension || '-' }}</p>
        </div>
      </div>

      <!-- Sintomas e Manifestações -->
      <div class="questionnaire-section">
        <h3>Sintomas e Manifestações</h3>
        <div class="qa-item">
          <p>
            <strong>Principais Sintomas:</strong>
            {{ formatMainSymptoms(voucherData?.mainSymptoms) }}
          </p>
          <p>
            <strong>Descrição dos Sintomas:</strong>
            {{ voucherData?.mainSymptomsDescription || '-' }}
          </p>
          <p>
            <strong>Manifestações Extra-intestinais:</strong>
            {{ voucherData?.extraIntestinalManifestations || '-' }}
          </p>
        </div>
      </div>

      <!-- Exames e Avaliações -->
      <div class="questionnaire-section">
        <h3>Exames e Avaliações</h3>
        <div class="qa-item">
          <p>
            <strong>Hemoglobina:</strong>
            {{ voucherData?.hemoglobin || '-' }} g/dL
          </p>
          <p><strong>VHS:</strong> {{ voucherData?.vhs || '-' }} mm/h</p>
          <p><strong>PCR:</strong> {{ voucherData?.pcr || '-' }} mg/dL</p>
          <p>
            <strong>Calprotectina Fecal:</strong>
            {{ voucherData?.fecalCalprotectin || '-' }} µg/g
          </p>
          <p>
            <strong>Albumina:</strong> {{ voucherData?.albumin || '-' }} g/dL
          </p>
        </div>
      </div>

      <!-- Tratamentos -->
      <div class="questionnaire-section">
        <h3>Tratamentos</h3>
        <div class="qa-item">
          <p>
            <strong>Tratamentos Prévios:</strong>
            {{ formatTreatments(voucherData?.previousTreatments) }}
          </p>
          <p>
            <strong>Descrição dos Tratamentos:</strong>
            {{ voucherData?.previousTreatmentsDescription || '-' }}
          </p>
          <p>
            <strong>Cirurgias Prévias:</strong>
            {{ formatYesNo(voucherData?.previousSurgeries) }}
          </p>
          <p>
            <strong>Descrição das Cirurgias:</strong>
            {{ voucherData?.previousSurgeriesDescription || '-' }}
          </p>
          <p>
            <strong>Tratamento Atual:</strong>
            {{ voucherData?.currentTreatment || '-' }}
          </p>
        </div>
      </div>
    </template>

    <!-- Observações e Dúvidas (comum para ambos) -->
    <div
      v-if="voucherData?.observations || voucherData?.questionsOrDoubts"
      class="questionnaire-section"
    >
      <h3>Observações e Dúvidas</h3>
      <div class="qa-item">
        <p v-if="voucherData?.observations">
          <strong>Observações:</strong> {{ voucherData?.observations }}
        </p>
        <p v-if="voucherData?.questionsOrDoubts">
          <strong>Perguntas ou Dúvidas:</strong>
          {{ voucherData?.questionsOrDoubts }}
        </p>
      </div>
    </div>

    <!-- Arquivos anexados -->
    <div v-if="hasFiles" class="questionnaire-section">
      <h3>Arquivos Anexados</h3>
      <div class="files-list">
        <div v-for="file in voucherData.files" :key="file.id" class="file-item">
          <span
            class="file-name"
            @click="handleDownload(file.id, file.fileName)"
          >
            {{ file.fileName }}
          </span>
        </div>
      </div>
    </div>

    <!-- Botão de Download PDF -->
    <div class="download-section">
      <el-button type="primary" @click="downloadPDF">
        <i class="el-icon-download"></i>
        Baixar Relatório em PDF
      </el-button>
    </div>
  </div>
</template>

<script>
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { mapActions } from 'vuex'

export default {
  name: 'QuestionnaireView',
  props: {
    voucherData: {
      type: Object,
      required: true
    }
  },
  computed: {
    isAsmaForm() {
      return this.voucherData?.diseaseName?.toLowerCase().includes('asma')
    },
    isCrohnForm() {
      return this.voucherData?.diseaseName?.toLowerCase().includes('crohn')
    },
    hasFiles() {
      return this.voucherData?.files?.length > 0
    }
  },
  methods: {
    ...mapActions('activeClinicalCases', ['fechVoucherDownload']),

    handleDownload(id, name) {
      const fileData = {
        id: id,
        name: name
      }
      this.fechVoucherDownload(fileData)
    },
    formatTreatments(treatments) {
      if (!treatments || !Array.isArray(treatments)) return '-'

      const treatmentMap = {
        1: 'Aminossalicilatos',
        2: 'Corticosteroides',
        3: 'Azatioprina',
        4: 'Metotrexato',
        5: 'Anti-TNF',
        6: 'Vedolizumabe',
        7: 'Ustekinumabe',
        8: 'Outros'
      }

      return treatments.map((id) => treatmentMap[id]).join(', ')
    },
    downloadPDF() {
      const doc = new jsPDF()

      // Cabeçalho
      doc.setFontSize(16)
      doc.text(`Caso Clínico nº ${this.voucherData?.voucherId}`, 14, 20)

      let currentY = 40

      // Informações básicas
      const basicInfo = [
        ['Especialidade', this.voucherData?.specialtyName || '-'],
        ['Doença', this.voucherData?.diseaseName || '-']
      ]

      doc.autoTable({
        startY: currentY,
        head: [['Campo', 'Informação']],
        body: basicInfo,
        theme: 'grid'
      })

      currentY = doc.lastAutoTable.finalY + 15

      // Dados do Paciente
      doc.setFontSize(14)
      doc.text('Dados do Paciente', 14, currentY)

      const patientData = [
        ['Idade', `${this.voucherData?.age || '-'} anos`],
        ['Gênero', this.formatGender(this.voucherData?.gender)],
        ['Cor/Raça', this.formatColor(this.voucherData?.color)]
      ]

      doc.autoTable({
        startY: currentY + 5,
        head: [['Campo', 'Informação']],
        body: patientData,
        theme: 'grid'
      })

      currentY = doc.lastAutoTable.finalY + 15

      // Seções específicas baseadas no tipo de formulário
      if (this.isAsmaForm) {
        // Diagnóstico de Asma Grave
        doc.text('Diagnóstico de Asma Grave', 14, currentY)
        const asmaData = [
          ['Diagnóstico de Asma', this.formatYesNo(this.voucherData?.asthma)],
          [
            'Outras Doenças Pulmonares',
            this.formatYesNo(this.voucherData?.lungDiseases)
          ],
          [
            'Adesão ao Tratamento',
            this.formatYesNo(this.voucherData?.asthmaTreatment)
          ],
          [
            'Técnica Inalatória Adequada',
            this.formatYesNo(this.voucherData?.inhalation)
          ]
        ]

        doc.autoTable({
          startY: currentY + 5,
          head: [['Campo', 'Informação']],
          body: asmaData,
          theme: 'grid'
        })

        // Comorbidades
        currentY = doc.lastAutoTable.finalY + 15
        doc.text('Comorbidades', 14, currentY)
        const comorbidityData = [
          [
            'Comorbidades Presentes',
            this.formatComorbidities(this.voucherData?.comorbidities)
          ],
          ['Outras Comorbidades', this.voucherData?.otherComorbidities || '-'],
          ['Informações Adicionais', this.voucherData?.comorbiditiesInfo || '-']
        ]

        doc.autoTable({
          startY: currentY + 5,
          head: [['Campo', 'Informação']],
          body: comorbidityData,
          theme: 'grid'
        })

        // Medicações e Tratamentos
        currentY = doc.lastAutoTable.finalY + 15
        doc.text('Medicações e Tratamentos', 14, currentY)
        const medicationData = [
          ['Uso de LABA', this.formatYesNo(this.voucherData?.laba)],
          ['Medicações para Asma', this.voucherData?.asthmaMedications || '-'],
          [
            'Uso de Imunobiológico',
            this.formatYesNo(this.voucherData?.immunobiological)
          ],
          [
            'Descrição do Imunobiológico',
            this.voucherData?.immunobiologicalDescription || '-'
          ],
          [
            'Uso de Corticoide Oral',
            this.formatYesNo(this.voucherData?.corticosteroid)
          ],
          [
            'Frequência de Corticoide Oral',
            this.voucherData?.corticosteroidTimes || '-'
          ],
          ['Medicações em Uso', this.voucherData?.medications || '-'],
          [
            'Descrição das Medicações',
            this.voucherData?.medicationsDescription || '-'
          ]
        ]

        doc.autoTable({
          startY: currentY + 5,
          head: [['Campo', 'Informação']],
          body: medicationData,
          theme: 'grid'
        })

        // Exames e Avaliações
        currentY = doc.lastAutoTable.finalY + 15
        doc.text('Exames e Avaliações', 14, currentY)
        const examData = [
          [
            'Data da Espirometria',
            this.formatDate(this.voucherData?.espiroDate)
          ],
          ['CVF Pré', this.voucherData?.preCvf || '-'],
          ['VEF1 Pré', this.voucherData?.preVef || '-'],
          ['VEF1/CVF Pré', this.voucherData?.preVefCvf || '-'],
          ['FEF 25-75% Pré', this.voucherData?.preFef || '-'],
          ['CVF Pós', this.voucherData?.posCvf || '-'],
          ['VEF1 Pós', this.voucherData?.posVef || '-'],
          ['VEF1/CVF Pós', this.voucherData?.posVefCvf || '-'],
          ['FEF 25-75% Pós', this.voucherData?.posFef || '-'],
          ['Resposta BD', this.voucherData?.bd || '-'],
          ['Eosinófilos no Sangue', this.voucherData?.eosinophilsBlood || '-'],
          ['IgE Total', this.voucherData?.igeTotal || '-'],
          ['FeNO', this.voucherData?.FeNO || '-']
        ]

        doc.autoTable({
          startY: currentY + 5,
          head: [['Campo', 'Informação']],
          body: examData,
          theme: 'grid'
        })

        // Histórico
        currentY = doc.lastAutoTable.finalY + 15
        doc.text('Histórico', 14, currentY)
        const historyData = [
          [
            'Hospitalizações',
            this.formatYesNo(this.voucherData?.hospitalization)
          ],
          [
            'Número de Hospitalizações',
            this.voucherData?.hospitalizationTimes || '-'
          ],
          ['Internações em UTI', this.formatYesNo(this.voucherData?.uti)],
          ['Número de Internações em UTI', this.voucherData?.utiTimes || '-'],
          [
            'Ventilação Mecânica',
            this.formatYesNo(this.voucherData?.ventilation)
          ],
          [
            'Número de Ventilações Mecânicas',
            this.voucherData?.ventilationTimes || '-'
          ]
        ]

        doc.autoTable({
          startY: currentY + 5,
          head: [['Campo', 'Informação']],
          body: historyData,
          theme: 'grid'
        })
      }

      if (this.isCrohnForm) {
        // Dados Antropométricos
        doc.text('Dados Antropométricos', 14, currentY)
        const anthropometricData = [
          ['Peso', `${this.voucherData?.weight || '-'} kg`],
          ['Altura', `${this.voucherData?.height || '-'} cm`]
        ]

        doc.autoTable({
          startY: currentY + 5,
          head: [['Campo', 'Informação']],
          body: anthropometricData,
          theme: 'grid'
        })

        // Informações do Diagnóstico
        currentY = doc.lastAutoTable.finalY + 15
        doc.text('Informações do Diagnóstico', 14, currentY)
        const diagnosisData = [
          ['Diagnóstico', this.formatDiagnostico(this.voucherData?.diag)],
          [
            'Idade no Início dos Sintomas',
            `${this.voucherData?.yearStartSint || '-'} anos`
          ],
          ['Idade no Diagnóstico', `${this.voucherData?.yearDiag || '-'} anos`],
          ['Localização Montreal', this.voucherData?.locationMontreal || '-'],
          ['Comportamento Montreal', this.voucherData?.behaviorMontreal || '-'],
          ['Extensão', this.voucherData?.extension || '-']
        ]

        doc.autoTable({
          startY: currentY + 5,
          head: [['Campo', 'Informação']],
          body: diagnosisData,
          theme: 'grid'
        })

        // Sintomas e Manifestações
        currentY = doc.lastAutoTable.finalY + 15
        doc.text('Sintomas e Manifestações', 14, currentY)
        const symptomsData = [
          [
            'Principais Sintomas',
            this.formatMainSymptoms(this.voucherData?.mainSymptoms)
          ],
          [
            'Descrição dos Sintomas',
            this.voucherData?.mainSymptomsDescription || '-'
          ],
          [
            'Manifestações Extra-intestinais',
            this.voucherData?.extraIntestinalManifestations || '-'
          ]
        ]

        doc.autoTable({
          startY: currentY + 5,
          head: [['Campo', 'Informação']],
          body: symptomsData,
          theme: 'grid'
        })

        // Exames e Avaliações
        currentY = doc.lastAutoTable.finalY + 15
        doc.text('Exames e Avaliações', 14, currentY)
        const examData = [
          ['Hemoglobina', `${this.voucherData?.hemoglobin || '-'} g/dL`],
          ['VHS', `${this.voucherData?.vhs || '-'} mm/h`],
          ['PCR', `${this.voucherData?.pcr || '-'} mg/dL`],
          [
            'Calprotectina Fecal',
            `${this.voucherData?.fecalCalprotectin || '-'} µg/g`
          ],
          ['Albumina', `${this.voucherData?.albumin || '-'} g/dL`]
        ]

        doc.autoTable({
          startY: currentY + 5,
          head: [['Campo', 'Informação']],
          body: examData,
          theme: 'grid'
        })

        // Tratamentos
        currentY = doc.lastAutoTable.finalY + 15
        doc.text('Tratamentos', 14, currentY)
        const treatmentData = [
          [
            'Tratamentos Prévios',
            this.formatTreatments(this.voucherData?.previousTreatments)
          ],
          [
            'Descrição dos Tratamentos',
            this.voucherData?.previousTreatmentsDescription || '-'
          ],
          [
            'Cirurgias Prévias',
            this.formatYesNo(this.voucherData?.previousSurgeries)
          ],
          [
            'Descrição das Cirurgias',
            this.voucherData?.previousSurgeriesDescription || '-'
          ],
          ['Tratamento Atual', this.voucherData?.currentTreatment || '-']
        ]

        doc.autoTable({
          startY: currentY + 5,
          head: [['Campo', 'Informação']],
          body: treatmentData,
          theme: 'grid'
        })
      }

      // Observações e Dúvidas (comum para ambos)
      currentY = doc.lastAutoTable.finalY + 15
      if (
        this.voucherData?.observations ||
        this.voucherData?.questionsOrDoubts
      ) {
        doc.text('Observações e Dúvidas', 14, currentY)
        const obsData = [
          ['Observações', this.voucherData?.observations || '-'],
          ['Perguntas ou Dúvidas', this.voucherData?.questionsOrDoubts || '-']
        ]

        doc.autoTable({
          startY: currentY + 5,
          head: [['Campo', 'Informação']],
          body: obsData,
          theme: 'grid'
        })
      }

      // Salvar o PDF
      doc.save(`caso_clinico_${this.voucherData?.voucherId}.pdf`)
    },
    formatYesNo(value) {
      return value === 1 ? 'Sim' : value === 2 ? 'Não' : '-'
    },
    formatGender(value) {
      return value === 1 ? 'Masculino' : value === 2 ? 'Feminino' : '-'
    },
    formatColor(value) {
      const colors = {
        1: 'Branca',
        2: 'Preta',
        3: 'Parda',
        4: 'Amarela',
        5: 'Indígena'
      }
      return colors[value] || '-'
    },
    formatComorbidities(comorbidities) {
      if (!comorbidities || !Array.isArray(comorbidities)) return '-'

      const comorbidityMap = {
        1: 'Nenhuma',
        2: 'Rinite alérgica',
        3: 'Dermatite atópica',
        4: 'Sinusopatia crônica com polipose nasal',
        6: 'Obesidade',
        7: 'Apneia obstrutiva do sono',
        8: 'Refluxo gastroesofágico',
        9: 'Hipertensão arterial sistêmica',
        10: 'Diabetes',
        11: 'Outras'
      }

      return comorbidities.map((id) => comorbidityMap[id]).join(', ')
    },
    formatDiagnostico(value) {
      const diagnosticos = {
        1: 'Doença de Crohn',
        2: 'Retocolite Ulcerativa'
      }
      return diagnosticos[value] || '-'
    },
    formatMainSymptoms(value) {
      const symptoms = {
        1: 'Diarreia',
        2: 'Dor abdominal',
        3: 'Perda de peso',
        4: 'Sangramento',
        5: 'Febre',
        6: 'Outros'
      }
      return symptoms[value] || '-'
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('pt-BR')
    }
  }
}
</script>

<style lang="scss" scoped>
.questionnaire-view {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;

  .questionnaire-header {
    margin-bottom: 30px;

    h2 {
      color: #2c3e50;
      margin-bottom: 15px;
    }

    .case-info {
      display: flex;
      gap: 20px;

      p {
        margin: 0;
      }
    }
  }

  .questionnaire-section {
    margin-bottom: 25px;

    h3 {
      color: #34495e;
      border-bottom: 2px solid #eee;
      padding-bottom: 8px;
      margin-bottom: 15px;
    }
  }

  .qa-item {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 8px;

    p {
      margin: 8px 0;
      line-height: 1.5;

      strong {
        color: #2c3e50;
        margin-right: 8px;
      }
    }
  }

  .files-list {
    .file-item {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;

      .file-name {
        color: #409eff;
        cursor: pointer;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}
</style>
